import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from '../hooks/useLocation';
import { supabaseLocationService } from '../services/supabaseLocationService';
import './LocationSelector.css';

interface LocationSelectorProps {
  onLocationSelect: (location: string) => void;
  placeholder?: string;
  className?: string;
  inputId?: string;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({ 
  onLocationSelect, 
  placeholder = "California City or County",
  className = "",
  inputId,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const { filterLocations } = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim()) {
      const filtered = filterLocations(value);
      setFilteredLocations(filtered);
      setShowDropdown(true);
    } else {
      setFilteredLocations([]);
      setShowDropdown(false);
    }
  };

  // Handle location selection
  const handleLocationSelect = (location: string) => {
    setInputValue(location);
    setShowDropdown(false);
    onLocationSelect(location);
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredLocations.length > 0) {
      handleLocationSelect(filteredLocations[0]);
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (inputValue.trim()) {
      const filtered = filterLocations(inputValue);
      setFilteredLocations(filtered);
      setShowDropdown(true);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`location-selector ${className}`}>
      <div className="input-container">
        {/* Decoy field prevents Chrome from showing saved-entry autocomplete over our dropdown */}
        <input
          type="text"
          name="climateplans-location-search-decoy"
          autoComplete="address-level2"
          tabIndex={-1}
          aria-hidden="true"
          className="location-input-decoy"
        />
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          name="climateplans-location-search"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="location-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
        />
        {showDropdown && filteredLocations.length > 0 && (
          <div ref={dropdownRef} className="autocomplete-dropdown">
            {filteredLocations.map((location, index) => {
              const locationInfo = supabaseLocationService.getAvailableLocations().find(loc => loc.name === location);
              return (
                <div
                  key={index}
                  className="autocomplete-item"
                  onClick={() => handleLocationSelect(location)}
                >
                  <div className="location-name">{location}</div>
                  {locationInfo && (
                    <div className="location-details">
                      <span className="location-type">{locationInfo.type}</span>
                      {locationInfo.type === 'city' && (
                        <span className="location-county">{locationInfo.county} County</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
